import * as React from 'react';
import { cn } from '@/lib/utils';

interface FieldControl {
  id: string;
  describedBy?: string;
  invalid: boolean;
  required: boolean;
}

const FieldContext = React.createContext<FieldControl | null>(null);

/**
 * The wiring a control needs for its `<Field>`'s label to actually name it.
 *
 * Threaded through context rather than cloned onto `children`, because the
 * control is often not the direct child: the barcode and tag-code fields wrap
 * their input in a positioning div, and `cloneElement` would hand the id to the
 * div. Context reaches through any depth of wrapper, so one change fixes every
 * shape of call site without editing any of them.
 *
 * The Field's id wins over an `id` set on the control, because the two must not
 * be able to disagree: the label points at the Field's id, so a control that
 * quietly kept its own would go back to being unlabelled. A caller that needs a
 * specific id sets `controlId` on the `Field`, which pins both ends at once.
 * The aria hooks work the other way — an explicit one is a deliberate override.
 */
function useFieldControl<
  T extends {
    id?: string;
    'aria-describedby'?: string;
    'aria-invalid'?: React.AriaAttributes['aria-invalid'];
    'aria-required'?: React.AriaAttributes['aria-required'];
  },
>(props: T): T {
  const field = React.useContext(FieldContext);
  if (!field) return props;
  return {
    ...props,
    id: field.id,
    'aria-describedby': props['aria-describedby'] ?? field.describedBy,
    'aria-invalid': props['aria-invalid'] ?? (field.invalid || undefined),
    'aria-required': props['aria-required'] ?? (field.required || undefined),
  };
}

export { useFieldControl };

export function Field({
  label,
  error,
  hint,
  required,
  controlId,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
  /** Pins the control's id when something outside has to point at it. */
  controlId?: string;
  children: React.ReactNode;
}) {
  const generated = React.useId();
  const id = controlId ?? generated;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  // One slot, and the error wins: a field showing both would move the layout on
  // every keystroke, and the rule matters less than the refusal.
  const describedBy = error ? errorId : hint ? hintId : undefined;

  const control = React.useMemo<FieldControl>(
    () => ({ id, describedBy, invalid: Boolean(error), required: Boolean(required) }),
    [id, describedBy, error, required],
  );

  return (
    <FieldContext.Provider value={control}>
      <div className="space-y-1.5">
        <label htmlFor={id} className="block text-sm font-medium">
          {label}
          {/* The star is `aria-hidden` and the requirement travels as
              `aria-required` on the control instead. Left readable it becomes
              part of the field's accessible name, which then no longer matches
              the visible label — "Branch name star". */}
          {required && (
            <span aria-hidden className="ms-0.5 text-red-600 dark:text-red-400">
              *
            </span>
          )}
        </label>
        {children}
        {error ? (
          <p id={errorId} className="text-xs text-red-600 dark:text-red-400">
            {error}
          </p>
        ) : hint ? (
          <p id={hintId} className="text-xs text-ink-muted">
            {hint}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

export const Select = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(({ className, ...props }, ref) => (
  <select
    ref={ref}
    className={cn(
      'h-11 w-full min-w-0 rounded-lg border border-line-strong bg-transparent px-3 text-sm',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      className,
    )}
    {...useFieldControl(props)}
  />
));
Select.displayName = 'Select';

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...props }, ref) => (
  <textarea
    ref={ref}
    className={cn(
      'w-full min-w-0 rounded-lg border border-line-strong bg-transparent px-3 py-2 text-sm',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      className,
    )}
    {...useFieldControl(props)}
  />
));
Textarea.displayName = 'Textarea';
